import { useAdminAuth } from "@/contexts/useAdminAuth";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import type { UserDetail } from "@/types";
import {
  ASSIGNABLE_ADMIN_ROLES,
  ORG_ROLES,
  ORG_ROLE_LABELS,
  normalizeOrgRole,
  type AssignableAdminRole,
  type OrgRole,
} from "@a7y/types";
import {
  Badge,
  Button,
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
  Field,
  Label,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
  Input,
  Textarea,
} from "@a7y/tailwind-catalyst";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Building2,
  Check,
  Copy,
  EllipsisVertical,
  KeyRound,
  Pencil,
  Shield,
  Trash2,
  User as UserIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

const ROLE_COLORS: Record<string, string> = {
  owner: "purple",
  manager: "blue",
  standard: "zinc",
};

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isSuperAdmin } = useAdminAuth();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmEmail, setDeleteConfirmEmail] = useState("");
  const [showEditName, setShowEditName] = useState(false);
  const [copiedField, setCopiedField] = useState<"id" | "email" | null>(null);

  const handleCopy = (field: "id" | "email", value: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    setTimeout(
      () => setCopiedField((current) => (current === field ? null : current)),
      2000,
    );
  };
  const [roleModalMembership, setRoleModalMembership] = useState<UserDetail["memberships"][number] | null>(null);

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteUser(userId!),
    onSuccess: (result) => {
      // A7Y-4614: a pending deletion revoked access but erased nothing shared —
      // stay on the page and say so instead of reporting it deleted.
      if (result?.pending) return;
      setShowDeleteConfirm(false);
      navigate("/admin/users");
    },
  });

  const { data, isLoading, error } = useQuery<UserDetail>({
    queryKey: ["user-detail", userId],
    queryFn: () => api.getUserDetail(userId!),
    enabled: !!userId,
  });

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 dark:border-zinc-600 border-t-blue-600" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="px-4 py-2">
        <Button plain onClick={() => navigate("/admin/users")}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Users
        </Button>
        <div className="mt-8 text-center">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
          <Text className="mt-2 text-gray-500 dark:text-zinc-400">User not found</Text>
        </div>
      </div>
    );
  }

  const { user, memberships } = data;
  const displayName = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email || user.id;

  return (
    <div className="px-4 py-2">
      {/* Header */}
      <div className="mb-8">
        <Button plain onClick={() => navigate("/admin/users")} className="mb-4">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Users
        </Button>
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100 dark:bg-blue-900/30">
            <UserIcon className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <Text className="text-2xl font-bold text-zinc-950 dark:text-white">{displayName}</Text>
            <Text className="text-sm text-zinc-500 dark:text-zinc-400">{user.email}</Text>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {user.accountDeletion && (
              <Badge color="amber" title={JSON.stringify(user.accountDeletion.holds)}>
                Deletion pending — access revoked
              </Badge>
            )}
            <Badge color={memberships.length > 0 ? "blue" : "zinc"}>
              {memberships.length} organization{memberships.length !== 1 ? "s" : ""}
            </Badge>
            <Button
              color="red"
              onClick={() => {
                setDeleteConfirmEmail("");
                setShowDeleteConfirm(true);
              }}
            >
              <Trash2 className="mr-1.5 h-4 w-4" />
              Delete User
            </Button>
          </div>
        </div>
      </div>

      {/* User Info */}
      <Section title="User Info" icon={<UserIcon className="h-5 w-5" />}>
        <DescriptionList>
          <DescriptionTerm>User ID</DescriptionTerm>
          <DescriptionDetails>
            <div className="flex items-center gap-2">
              <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5 text-sm">{user.id}</code>
              <button
                type="button"
                onClick={() => handleCopy("id", user.id)}
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                title="Copy user ID"
              >
                {copiedField === "id" ? (
                  <Check className="h-3.5 w-3.5 text-green-500" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
          </DescriptionDetails>

          <DescriptionTerm>Email</DescriptionTerm>
          <DescriptionDetails>
            <div className="flex items-center gap-2">
              <span>{user.email || "—"}</span>
              {user.email && (
                <button
                  type="button"
                  onClick={() => handleCopy("email", user.email!)}
                  className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                  title="Copy email"
                >
                  {copiedField === "email" ? (
                    <Check className="h-3.5 w-3.5 text-green-500" />
                  ) : (
                    <Copy className="h-3.5 w-3.5" />
                  )}
                </button>
              )}
            </div>
          </DescriptionDetails>

          <DescriptionTerm>First Name</DescriptionTerm>
          <DescriptionDetails>
            {user.firstName || "—"}
            <button
              type="button"
              className="ml-2 inline-flex items-center text-indigo-600 hover:text-indigo-500"
              onClick={() => setShowEditName(true)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </DescriptionDetails>

          <DescriptionTerm>Last Name</DescriptionTerm>
          <DescriptionDetails>
            {user.lastName || "—"}
            <button
              type="button"
              className="ml-2 inline-flex items-center text-indigo-600 hover:text-indigo-500"
              onClick={() => setShowEditName(true)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </DescriptionDetails>

          {user.orgId && (
            <>
              <DescriptionTerm>Default Org ID</DescriptionTerm>
              <DescriptionDetails>
                <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5 text-sm">{user.orgId}</code>
              </DescriptionDetails>
            </>
          )}

          <DescriptionTerm>Created</DescriptionTerm>
          <DescriptionDetails>
            {user.createdAt ? formatDate(user.createdAt) : "—"}
          </DescriptionDetails>

          {user.updatedAt && (
            <>
              <DescriptionTerm>Updated</DescriptionTerm>
              <DescriptionDetails>{formatDate(user.updatedAt)}</DescriptionDetails>
            </>
          )}
        </DescriptionList>
      </Section>

      {/* A7Y-4957: admin-site access — superadmin viewers, staff accounts only */}
      {isSuperAdmin && isStaffEmail(user.email) && (
        <div className="mt-8">
          <AdminAccessSection
            userId={userId!}
            superadmin={user.superadmin === true}
            adminRole={user.adminRole ?? null}
            onSaved={() => queryClient.invalidateQueries({ queryKey: ["user-detail", userId] })}
          />
        </div>
      )}

      {/* Organization Memberships */}
      <div className="mt-8">
        <Section title={`Organization Memberships (${memberships.length})`} icon={<Building2 className="h-5 w-5" />}>
          <Table className="[--gutter:--spacing(6)] sm:[--gutter:--spacing(8)]">
            <TableHead>
              <TableRow>
                <TableHeader>Organization</TableHeader>
                <TableHeader>Role</TableHeader>
                <TableHeader>Joined</TableHeader>
                <TableHeader className="text-right">Actions</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {memberships.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8">
                    <Building2 className="mx-auto h-8 w-8 text-zinc-400" />
                    <Text className="mt-1 text-sm text-zinc-500">
                      User is not a member of any organization
                    </Text>
                  </TableCell>
                </TableRow>
              ) : (
                memberships.map((membership) => (
                  <MembershipRow
                    key={membership.membershipId}
                    userId={userId!}
                    membership={membership}
                    onMutated={() =>
                      queryClient.invalidateQueries({ queryKey: ["user-detail", userId] })
                    }
                    onOpenRoleModal={() => setRoleModalMembership(membership)}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </Section>
      </div>

      {/* Change Role Modal — rendered outside the table to avoid invalid DOM nesting */}
      {roleModalMembership && (
        <ChangeRoleDialog
          userId={userId!}
          membership={roleModalMembership}
          onClose={() => setRoleModalMembership(null)}
          onSuccess={() => {
            setRoleModalMembership(null);
            queryClient.invalidateQueries({ queryKey: ["user-detail", userId] });
          }}
        />
      )}

      {/* Signup Context */}
      <div className="mt-8">
        <Section title="Signup Context" icon={<UserIcon className="h-5 w-5" />}>
          {user.signupContext && Object.keys(user.signupContext).length > 0 ? (
            <DescriptionList>
              {Object.entries(user.signupContext).map(([key, value]) => (
                <div key={key} className="contents">
                  <DescriptionTerm>{key}</DescriptionTerm>
                  <DescriptionDetails>
                    <code className="rounded bg-zinc-100 dark:bg-zinc-700 px-1.5 py-0.5 text-sm">
                      {typeof value === "object" ? JSON.stringify(value) : String(value)}
                    </code>
                  </DescriptionDetails>
                </div>
              ))}
            </DescriptionList>
          ) : (
            <Text className="text-zinc-400">No signup context data available.</Text>
          )}
        </Section>
      </div>

      {/* Edit Name Dialog */}
      <EditNameDialog
        open={showEditName}
        onClose={() => setShowEditName(false)}
        userId={userId!}
        currentFirstName={user.firstName ?? ""}
        currentLastName={user.lastName ?? ""}
        onSuccess={() => {
          setShowEditName(false);
          queryClient.invalidateQueries({ queryKey: ["user-detail", userId] });
          queryClient.invalidateQueries({ queryKey: ["users"] });
          queryClient.invalidateQueries({ queryKey: ["users-lifecycle"] });
        }}
      />

      {/* Delete User Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} size="sm">
        <DialogTitle>Delete User</DialogTitle>
        <DialogBody>
          <Text className="text-sm text-zinc-500 dark:text-zinc-400">
            This action is <strong className="text-red-600">permanent and irreversible</strong>. Deleting this user will:
          </Text>
          <ul className="mt-2 list-disc pl-5 text-sm text-zinc-500 dark:text-zinc-400 space-y-1">
            <li>Remove the user from Clerk authentication and revoke their access</li>
            <li>Delete their personal data and personal projects</li>
            <li>Remove them from their organizations, keeping organization projects and credentials other members use</li>
          </ul>
          <Field className="mt-4">
            <Label>
              Type <strong>{user.email}</strong> to confirm
            </Label>
            <Input
              value={deleteConfirmEmail}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeleteConfirmEmail(e.target.value)}
              placeholder={user.email ?? ""}
            />
          </Field>
          {deleteMutation.data?.pending && (
            <Text className="mt-2 text-xs text-amber-600">
              Access revoked. Deletion is pending: {deleteMutation.data.errors.join("; ")}
            </Text>
          )}
          {deleteMutation.isError && (
            <Text className="mt-2 text-xs text-red-600">
              Failed: {(deleteMutation.error as Error)?.message}
            </Text>
          )}
        </DialogBody>
        <DialogActions>
          <Button outline onClick={() => setShowDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button
            color="red"
            disabled={deleteConfirmEmail !== user.email || deleteMutation.isPending}
            onClick={() => deleteMutation.mutate()}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete User"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/** Mirrors the API's rule: admin-site roles go to @apptonomy.ai staff accounts only. */
function isStaffEmail(email: string | undefined): boolean {
  return (email ?? "").toLowerCase().endsWith("@apptonomy.ai");
}

const ADMIN_ROLE_LABELS: Record<AssignableAdminRole, string> = {
  marketing: "Marketing — content, CMO, read-only customer lookup",
};

export function AdminAccessSection({
  userId,
  superadmin,
  adminRole,
  onSaved,
}: {
  userId: string;
  superadmin: boolean;
  adminRole: AssignableAdminRole | null;
  onSaved: () => void;
}) {
  const [selected, setSelected] = useState<AssignableAdminRole | "">(adminRole ?? "");

  useEffect(() => {
    // Re-sync after a save refetches the user (or another admin changed it).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelected(adminRole ?? "");
  }, [adminRole]);

  const mutation = useMutation({
    mutationFn: () => api.setUserAdminRole(userId, selected === "" ? null : selected),
    onSuccess: onSaved,
  });

  return (
    <Section title="Admin Access" icon={<KeyRound className="h-5 w-5" />}>
      {superadmin ? (
        <Text className="text-sm text-zinc-500 dark:text-zinc-400" data-testid="admin-access-superadmin">
          Superadmin — full admin access. A role set here would have no effect.
        </Text>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <Field className="min-w-72 flex-1">
            <Label>Admin site role</Label>
            <Select
              aria-label="Admin site role"
              value={selected}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setSelected(e.target.value as AssignableAdminRole | "")
              }
            >
              <option value="">None — no admin access</option>
              {ASSIGNABLE_ADMIN_ROLES.map((role) => (
                <option key={role} value={role}>
                  {ADMIN_ROLE_LABELS[role]}
                </option>
              ))}
            </Select>
          </Field>
          <Button
            color="blue"
            disabled={selected === (adminRole ?? "") || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? "Saving..." : "Save"}
          </Button>
        </div>
      )}
      {mutation.isError && (
        <Text className="mt-2 text-xs text-red-600">
          Failed: {(mutation.error as Error)?.message}
        </Text>
      )}
    </Section>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg bg-white dark:bg-zinc-800 p-6 shadow-sm ring-1 ring-zinc-200 dark:ring-zinc-700">
      <div className="mb-4 flex items-center gap-2">
        <span className="text-zinc-950 dark:text-white">{icon}</span>
        <span className="text-base font-semibold text-zinc-950 dark:text-white">{title}</span>
      </div>
      {children}
    </div>
  );
}

function EditNameDialog({
  open,
  onClose,
  userId,
  currentFirstName,
  currentLastName,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  userId: string;
  currentFirstName: string;
  currentLastName: string;
  onSuccess: () => void;
}) {
  const [firstName, setFirstName] = useState(currentFirstName);
  const [lastName, setLastName] = useState(currentLastName);

  useEffect(() => {
    if (open) {
      // Rendered unconditionally at UserDetailPage.tsx:315, so this dialog never
      // unmounts and the useState initializers run once for the page's lifetime.
      // Resetting on open is what makes a reopen after a cancelled edit show the
      // stored name. key={String(open)} would remount on close too, blanking the
      // inputs during the Headless UI exit transition.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFirstName(currentFirstName);
      setLastName(currentLastName);
    }
  }, [open, currentFirstName, currentLastName]);

  const mutation = useMutation({
    mutationFn: () => api.updateUserName(userId, { firstName, lastName }),
    onSuccess,
  });

  const hasChanges = firstName !== currentFirstName || lastName !== currentLastName;

  return (
    <Dialog open={open} onClose={onClose} size="sm">
      <DialogTitle>Edit Name</DialogTitle>
      <DialogBody>
        <Field>
          <Label>First Name</Label>
          <Input
            value={firstName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
          />
        </Field>
        <Field className="mt-4">
          <Label>Last Name</Label>
          <Input
            value={lastName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
          />
        </Field>
        {mutation.isError && (
          <Text className="mt-2 text-xs text-red-600">
            Failed: {(mutation.error as Error)?.message}
          </Text>
        )}
      </DialogBody>
      <DialogActions>
        <Button outline onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="blue"
          disabled={!hasChanges || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function MembershipRow({
  userId,
  membership,
  onMutated,
  onOpenRoleModal,
}: {
  userId: string;
  membership: UserDetail["memberships"][number];
  onMutated: () => void;
  onOpenRoleModal: () => void;
}) {
  const navigate = useNavigate();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [reason, setReason] = useState("");

  const removeMutation = useMutation({
    mutationFn: () =>
      api.removeUserFromOrg(userId, membership.membershipId, reason),
    onSuccess: () => {
      setShowRemoveConfirm(false);
      setReason("");
      onMutated();
    },
  });

  return (
    <TableRow>
      <TableCell className="font-medium">
        <div>
          <button
            type="button"
            className="text-sm font-medium text-indigo-600 hover:text-indigo-500"
            onClick={() => navigate(`/admin/organizations/${membership.orgId}`)}
          >
            {membership.orgNameResolved ? (
              membership.orgName
            ) : (
              <span className="italic text-zinc-500">{membership.orgName}</span>
            )}
          </button>
          <div className="text-xs font-mono text-zinc-500">{membership.orgId}</div>
        </div>
      </TableCell>
      <TableCell>
        <Badge color={ROLE_COLORS[membership.role] ?? "zinc"}>
          {membership.role.toUpperCase()}
        </Badge>
      </TableCell>
      <TableCell className="text-zinc-500">
        {formatDate(membership.joinedAt)}
      </TableCell>
      <TableCell className="text-right">
        {showRemoveConfirm ? (
          <div className="flex items-center justify-end gap-2">
            <Field className="flex-1">
              <Label className="sr-only">Reason</Label>
              <Textarea
                value={reason}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
                placeholder="Reason for removal..."
                rows={1}
                className="text-sm"
              />
            </Field>
            <Button
              color="red"
              disabled={!reason.trim() || removeMutation.isPending}
              onClick={() => removeMutation.mutate()}
            >
              {removeMutation.isPending ? "..." : "Confirm"}
            </Button>
            <Button
              outline
              onClick={() => {
                setShowRemoveConfirm(false);
                setReason("");
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Dropdown>
            <DropdownButton plain aria-label="Membership actions">
              <EllipsisVertical className="h-4 w-4" />
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
              <DropdownItem onClick={onOpenRoleModal}>
                <Shield data-slot="icon" />
                Change Role
              </DropdownItem>
              <DropdownItem onClick={() => setShowRemoveConfirm(true)}>
                <Trash2 data-slot="icon" className="text-red-500" />
                <span className="text-red-500">Remove User</span>
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        )}
        {removeMutation.isError && (
          <Text className="mt-1 text-xs text-red-600">
            Failed: {(removeMutation.error as Error)?.message}
          </Text>
        )}
      </TableCell>
    </TableRow>
  );
}

function ChangeRoleDialog({
  userId,
  membership,
  onClose,
  onSuccess,
}: {
  userId: string;
  membership: UserDetail["memberships"][number];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [selectedRole, setSelectedRole] = useState<OrgRole>(normalizeOrgRole(membership.role) ?? "standard");

  const roleMutation = useMutation({
    mutationFn: () =>
      api.changeUserRole(userId, membership.membershipId, selectedRole),
    onSuccess,
  });

  return (
    <Dialog open onClose={onClose} size="sm">
      <DialogTitle>Change Role</DialogTitle>
      <DialogBody>
        <Text className="mb-4 text-sm text-zinc-500 dark:text-zinc-400">
          Update the role for this user in{" "}
          {membership.orgNameResolved ? (
            <strong>{membership.orgName}</strong>
          ) : (
            <>
              <strong className="italic">{membership.orgName}</strong>{" "}
              <span className="font-mono">({membership.orgId})</span>
            </>
          )}
          .
        </Text>
        <Field>
          <Label>Role</Label>
          <Select
            value={selectedRole}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setSelectedRole(e.target.value as OrgRole)
            }
          >
            {ORG_ROLES.map((role) => (
              <option key={role} value={role}>
                {ORG_ROLE_LABELS[role]}
              </option>
            ))}
          </Select>
        </Field>
        {roleMutation.isError && (
          <Text className="mt-2 text-xs text-red-600">
            Failed: {(roleMutation.error as Error)?.message}
          </Text>
        )}
      </DialogBody>
      <DialogActions>
        <Button outline onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="blue"
          disabled={selectedRole === membership.role || roleMutation.isPending}
          onClick={() => roleMutation.mutate()}
        >
          {roleMutation.isPending ? "Updating..." : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
